import React, { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { FoldableNumberInput } from './FoldableNumberInput';
import { useMediaQuery } from 'react-responsive';
import classnames from 'classnames';
import { DurationButton } from 'components/Duration';
import { useTrackSessionCreationStep } from '@app/analytics';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.marginBase * 2,
    width: '100%',
    [theme.media.desktop]: {
      flexWrap: 'nowrap',
      height: theme.marginBase * 9,
    },
  },
  durationsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.marginBase * 2 + 'px',
    [theme.media.mobile]: {
      flexWrap: 'wrap',
    },
  },
  fullWidth: {
    width: '100%',
  },
  durationIcon: {
    fill: theme.colors.grayBorder,
  },
}));

interface Props {
  name: string;
  buttonDurations: number[];
  defaultDuration?: number;
}

export const DurationSelectors = ({
  name,
  buttonDurations,
  defaultDuration,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const formik = useFormikContext<any>();
  const [isCustomDurationFolded, setIsCustomDurationFolded] = useState(true);
  const track = useTrackSessionCreationStep();

  const setValue = useCallback(
    (value: number) => {
      track('Duration', { duration: value });
      if (!formik || !name) {
        return;
      }
      formik.setFieldValue(name, value);
    },
    [formik, name],
  );

  const onDurationClick = useCallback(
    (value: number) => {
      setIsCustomDurationFolded(true);
      setValue(value);
    },
    [setValue],
  );

  const isDesktop = useMediaQuery({ query: theme.mediaContent.desktop });

  const unfoldCustomDuration = useCallback((value: boolean) => {
    setIsCustomDurationFolded(value);
  }, []);

  return (
    <div className={classes.container}>
      <div
        className={classnames(classes.durationsContainer, {
          [classes.fullWidth]: isCustomDurationFolded || !isDesktop,
        })}
      >
        {buttonDurations.map((duration) => (
          <DurationButton
            duration={duration}
            onClick={() => onDurationClick(duration)}
            title={`add ${duration}min`}
            isSelected={formik.values.duration === duration}
            hideLabel={isDesktop && !isCustomDurationFolded}
            trackingName="Duration"
          />
        ))}
      </div>

      <FoldableNumberInput
        unit="min"
        name={name}
        step={5}
        min={1}
        initialValue={defaultDuration}
        isFolded={isCustomDurationFolded}
        setIsFolded={unfoldCustomDuration}
        trackingName="Duration"
      />
    </div>
  );
};
